import { useState } from 'react';
import type { Team } from '@nepal-football/shared';

import { useDeleteTeam, useTeams } from '../api/hooks';
import { useAdminAuth } from '../hooks/AdminAuthContext';
import TeamForm from '../components/TeamForm';

export default function Teams() {
  const { isAdmin } = useAdminAuth();
  const { data: teams, isLoading, isError } = useTeams();
  const deleteMutation = useDeleteTeam();

  const [formMode, setFormMode] = useState<
    | { kind: 'closed' }
    | { kind: 'create' }
    | { kind: 'edit'; team: Team }
  >({ kind: 'closed' });

  if (isLoading) return <div className="inline-loading">Loading teams…</div>;
  if (isError || !teams) return <div className="inline-error">Team information is unavailable.</div>;

  function handleDelete(team: Team) {
    const ok = window.confirm(`Remove team "${team.name}"?`);
    if (ok) deleteMutation.mutate(team.id);
  }

  return (
    <div>
      {isAdmin && (
        <div className="teams-admin-bar">
          <button
            type="button"
            className="reg-btn reg-btn-compact"
            onClick={() => setFormMode({ kind: 'create' })}
          >
            + Add Team
          </button>
        </div>
      )}

      <div className="teams-grid">
        {teams.map((team) => {
          const hasRegistrationDetails =
            !!team.city || !!team.captainName ||
            (team.members && team.members.length > 0) ||
            !!team.instagramUrl;

          return (
            <div
              key={team.id}
              className="team-card"
              style={{ borderLeftColor: team.color }}
            >
              <div className="team-header">
                <div className="team-icon" aria-hidden="true">{team.icon}</div>
                <div className="team-header-titles">
                  <div className="team-name">{team.name}</div>
                  <div className="team-count">
                    👤 {team.countLabel ?? team.count} volunteers needed
                  </div>
                </div>
                {isAdmin && (
                  <div className="team-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => setFormMode({ kind: 'edit', team })}
                      aria-label={`Edit ${team.name}`}
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => handleDelete(team)}
                      aria-label={`Delete ${team.name}`}
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>

              {hasRegistrationDetails && (
                <div className="team-details">
                  {team.city && (
                    <div className="team-detail-row">
                      <span className="team-detail-label">City</span>
                      <span>{team.city}</span>
                    </div>
                  )}
                  {team.captainName && (
                    <div className="team-detail-row">
                      <span className="team-detail-label">Captain</span>
                      <span>{team.captainName}</span>
                    </div>
                  )}
                  {team.instagramUrl && (
                    <div className="team-detail-row">
                      <span className="team-detail-label">Instagram</span>
                      <a
                        href={team.instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="team-detail-link"
                      >
                        @{team.instagramUrl.replace(/^https?:\/\/(www\.)?instagram\.com\//i, '').replace(/\/$/, '')}
                      </a>
                    </div>
                  )}
                  {team.members && team.members.length > 0 && (
                    <div className="team-detail-row team-detail-members">
                      <span className="team-detail-label">Members</span>
                      <div className="team-member-tags">
                        {team.members.map((m, i) => (
                          <span key={i} className="team-member-tag">{m}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {team.tasks.slice(0, 5).map((task) => (
                <div
                  key={task}
                  className="team-task-item"
                  style={{ borderLeftColor: `${team.color}60` }}
                >
                  {task}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {formMode.kind !== 'closed' && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={formMode.kind === 'edit' ? 'Edit team' : 'Add team'}
          onClick={(e) => {
            if (e.target === e.currentTarget) setFormMode({ kind: 'closed' });
          }}
        >
          <div className="modal-box">
            <TeamForm
              team={formMode.kind === 'edit' ? formMode.team : undefined}
              onSave={() => setFormMode({ kind: 'closed' })}
              onCancel={() => setFormMode({ kind: 'closed' })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
